import { Constants as AgGridConstants, GridOptions, RowNode } from 'ag-grid-community';
import { CustomColDef } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { AppSearchColumnFields } from './app-search-grid-options-formatter';
import { BiosampleSearchColumnFields } from './biosample-search-grid-options-formatter';
import { BiosampleLibraryPrepSearchColumnFields } from './biosample-library-prep-search-grid-options-formatter';
import { FileSearchColumnFields } from './file-search-grid-options-formatter';
import { ProjectSearchColumnFields } from './project-search-grid-options-formatter';
import { RunSearchColumnFields } from './run-search-grid-options-formatter';
import { SampleSearchColumnFields } from './sample-search-grid-options-formatter';
import { DatasetSearchColumnFields } from './dataset-search-grid-options-formatter';
import { AppResultFileSearchColumnFields } from './app-result-file-search-grid-options-formatter';
import { AppResultSearchColumnFields } from './app-result-search-grid-options-formatter';
import IResource from '@app/core/model/resource';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { ISample } from '@app/core/model/samples/sample';

export class Permissions {
    static OWNER = "owner";
    static READ = "read";
}

export class ColumnMinWidth {
    static SIZE = 110
    static DATE = 190
    static RESOURCE = 300
    static RUN_STATUS = 200
}

export type SearchColumnFields =
    ProjectSearchColumnFields |
    RunSearchColumnFields |
    AppSearchColumnFields |
    BiosampleSearchColumnFields |
    BiosampleLibraryPrepSearchColumnFields |
    SampleSearchColumnFields |
    FileSearchColumnFields |
    AppResultFileSearchColumnFields |
    DatasetSearchColumnFields |
    AppResultSearchColumnFields;
export interface OverrideColumnDefinition<ColumnFields extends SearchColumnFields> extends CustomColDef {
    colId: ColumnFields;
    showAlways?: boolean;
}

export interface OverrideColumnParameters<ColumnFields extends SearchColumnFields> {
    inclusiveColumns?: SearchColumnFields[];
    overrideColumnDefinitions?: OverrideColumnDefinition<ColumnFields>[];
}
export abstract class SearchGridOptionsFormatter<ColumnFields extends SearchColumnFields> {
    public gridOptions: GridOptions;
    public resourceConfig: ISearchResourceConfig;

    public rowSelectionParams?: { 
        requiredPermissions?: Permissions;
        supportedGenomeBuilds?: string[];
        currentUserId?: string;
     }

    constructor(
        public searchResourceDictionaryService: SearchResourceDictionaryService,
        public resourceType: SearchResourceType
    ) {
        this.resourceConfig = this.searchResourceDictionaryService.getResourceConfigByType(this.resourceType);
    }

    public getGridOptions(
        overrideGridOptions?: Partial<GridOptions>,
    ): GridOptions {
        const defaultGridOptions = {
            rowModelType: AgGridConstants.ROW_MODEL_TYPE_SERVER_SIDE,
            columnDefs: this.getColumnDefinitions(),
            rowData: null,
            rowSelection: 'single',
            domLayout: 'autoHeight',
            suppressRowClickSelection: true,
            pagination: true,
            paginationPageSize: 25,
            cacheBlockSize: 100,
            serverSideSortingAlwaysResets: true,
            onGridReady: (event) => {
                event.api.sizeColumnsToFit();
                this.gridOptions.api = event.api;
            },
            isRowSelectable: (node) => {
                return this.isRowSelectable(node);
            },
            getRowClass: (params) => {
                return this.isRowSelectable(params.node) ? '' : 'row-unselectable';
            },
    };
        this.gridOptions = Object.assign(defaultGridOptions, overrideGridOptions);
        return this.gridOptions;
    };

    protected abstract getDefaultColumnDefinitions(): CustomColDef[];
    protected abstract getOverrideColumnParameters(): OverrideColumnParameters<SearchColumnFields>;

    public getColumnDefinitions(): CustomColDef[] {
        const defaultColumnDefinitions = this.getDefaultColumnDefinitions();
        const overrideColumnParameters = this.getOverrideColumnParameters();
        if(!overrideColumnParameters) {
            return defaultColumnDefinitions;
        } else {
            const columnDefinitions = this.applyOverridingColumnParameters(
                defaultColumnDefinitions,
                overrideColumnParameters
            );
            return columnDefinitions;
        }
    }

    canUserAccessOwnableResource(resource: IResource): boolean {
        if(
            this.rowSelectionParams &&
            this.rowSelectionParams.currentUserId &&
            this.rowSelectionParams.requiredPermissions && 
            this.rowSelectionParams.requiredPermissions === Permissions.OWNER
        ) {
            return !resource.UserOwnedBy 
            || !resource.UserOwnedBy.Id 
            || String(resource.UserOwnedBy.Id) === String(this.rowSelectionParams.currentUserId)
        } else {
            return true;
        }
    }

    isSampleFromSupportedGenome(sample: ISample): boolean {
        if(
            this.rowSelectionParams &&
            this.rowSelectionParams.supportedGenomeBuilds
        ) {
            if(!sample.Genome) {
                return false;
            }
            return this.rowSelectionParams.supportedGenomeBuilds.findIndex((genomeBuild) => genomeBuild === sample.Genome.Build) > -1;
        } else {
            return true;
        }
    }

    getUnselectableRowMessage(node: RowNode): string {
        if(node && node.data) {
            const resource = this.searchResourceDictionaryService.getSearchResultResource(node.data, this.resourceType);
            if (!this.canUserAccessOwnableResource(resource)) {
                return `Only ${this.resourceConfig.displayNamePlural} you own may be selected.`;
            }

            if(!this.isSampleFromSupportedGenome(resource)) {
                return `Only ${this.resourceConfig.displayNamePlural} from supported genomes may be selected.`;
            }

            return null;
        }
    }

    isRowSelectable(node: RowNode): boolean {
        if(
            node && 
            node.data && 
            this.rowSelectionParams
        ) {
            const resource = this.searchResourceDictionaryService.getSearchResultResource(node.data, this.resourceType);
            const canUserAccessResource = this.rowSelectionParams.requiredPermissions === Permissions.OWNER ? this.canUserAccessOwnableResource(resource) : true;

            const isResourceSample = this.resourceType === SearchResourceType.Sample;
            let isSampleFromSupportedGenome;
            if(isResourceSample) {
                isSampleFromSupportedGenome = this.isSampleFromSupportedGenome(resource);
            }

            return canUserAccessResource && (isResourceSample ? isSampleFromSupportedGenome : true);
        } else {
          return true;
        }
      }

    private applyOverridingColumnParameters(
        defaultColumnDefinitions: CustomColDef[],
        overrideColumnParameters?: OverrideColumnParameters<SearchColumnFields>
    ): CustomColDef[] {
        let updatedColumnDefinitions = defaultColumnDefinitions;
        const { inclusiveColumns, overrideColumnDefinitions } = overrideColumnParameters;

        if(inclusiveColumns) {
            updatedColumnDefinitions = [];
            inclusiveColumns.forEach((columnField) => {
                const columnDefinition = defaultColumnDefinitions
                    .find((defaultColumnDefinition) => defaultColumnDefinition.colId === columnField);

                if(!columnDefinition) {
                    throw new Error(`Could not find column definition for ${columnField}`);
                }
                updatedColumnDefinitions.push(columnDefinition);
            });
        }

        if(overrideColumnDefinitions) {
            overrideColumnDefinitions.forEach((overrideColumnDefinition: OverrideColumnDefinition<ColumnFields>) => {
                const updatedColumnDefinition = updatedColumnDefinitions
                    .find((columnDefinition) => columnDefinition.colId === overrideColumnDefinition.colId);

                if(!updatedColumnDefinition) {
                    throw new Error(`Could not find column definition for ${overrideColumnDefinition.colId}`);
                }
                Object.assign(updatedColumnDefinition, overrideColumnDefinition);

                // ag-grid does not allow mixing of cellRendererSelector and cellRendererFramework
                if (overrideColumnDefinition.cellRendererSelector && updatedColumnDefinition.cellRendererFramework) {
                    delete updatedColumnDefinition.cellRendererFramework;
                } else if (overrideColumnDefinition.cellRendererFramework && updatedColumnDefinition.cellRendererSelector) {
                    delete updatedColumnDefinition.cellRendererSelector;
                }
            });
        }

        return updatedColumnDefinitions;
    }


}


